<template>
  <div :class="wrapperClasses">
    <!-- Label -->
    <label v-if="label" :for="id" :class="labelClasses">
      {{ label }}
      <span v-if="required" class="text-danger ml-0.5">*</span>
    </label>

    <!-- Input wrapper (for icons) -->
    <div class="relative">
      <!-- Icon Left -->
      <div v-if="iconLeft || $slots['icon-left']" class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <slot name="icon-left">
          <span class="text-gray-400" v-html="iconLeft"></span>
        </slot>
      </div>

      <!-- Input element -->
      <input
        :id="id"
        :type="type"
        :value="modelValue"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        :required="required"
        :min="min"
        :max="max"
        :step="step"
        :autocomplete="autocomplete"
        :class="inputClasses"
        @input="handleInput"
        @blur="handleBlur"
        @focus="handleFocus"
        @keyup.enter="handleEnter"
      />

      <!-- Icon Right -->
      <div v-if="iconRight || clearable || $slots['icon-right']" class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-auto">
        <!-- Clear button -->
        <button
          v-if="clearable && modelValue"
          type="button"
          class="text-gray-400 hover:text-gray-600 focus:outline-none"
          @click="handleClear"
        >
          <i class="fas fa-times-circle text-sm"></i>
        </button>

        <!-- Icon Right slot -->
        <slot v-else name="icon-right">
          <span v-if="iconRight" class="text-gray-400" v-html="iconRight"></span>
        </slot>
      </div>
    </div>

    <!-- Helper text or error message -->
    <p v-if="error" class="mt-1 text-xs text-danger">{{ error }}</p>
    <p v-else-if="hint" class="mt-1 text-xs text-gray-500">{{ hint }}</p>
  </div>
</template>

<script>
export default {
  name: 'BaseInput',
  props: {
    modelValue: {
      type: [String, Number],
      default: ''
    },
    id: {
      type: String,
      default: () => `input-${Math.random().toString(36).substr(2, 9)}`
    },
    type: {
      type: String,
      default: 'text',
      validator: (value) => [
        'text',
        'email',
        'password',
        'number',
        'tel',
        'url',
        'search',
        'date',
        'time',
        'datetime-local'
      ].includes(value)
    },
    label: {
      type: String,
      default: null
    },
    placeholder: {
      type: String,
      default: ''
    },
    hint: {
      type: String,
      default: null
    },
    error: {
      type: String,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: 'md',
      validator: (value) => ['sm', 'md', 'lg'].includes(value)
    },
    iconLeft: {
      type: String,
      default: null
    },
    iconRight: {
      type: String,
      default: null
    },
    clearable: {
      type: Boolean,
      default: false
    },
    min: {
      type: [String, Number],
      default: null
    },
    max: {
      type: [String, Number],
      default: null
    },
    step: {
      type: [String, Number],
      default: null
    },
    autocomplete: {
      type: String,
      default: 'off'
    }
  },
  computed: {
    wrapperClasses() {
      return 'w-full'
    },
    labelClasses() {
      return 'block mb-1.5 text-sm font-medium text-gray-700'
    },
    inputClasses() {
      return [
        'block w-full border rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary',
        this.sizeClasses,
        this.iconLeft || this.$slots['icon-left'] ? 'pl-10' : '',
        this.iconRight || this.clearable ? 'pr-10' : '',
        this.error ? 'border-danger focus:ring-red-500/20 focus:border-danger' : 'border-gray-300',
        this.disabled ? 'bg-gray-50 cursor-not-allowed opacity-60' : 'bg-white',
        this.readonly ? 'bg-gray-50' : ''
      ]
    },
    sizeClasses() {
      const sizes = {
        sm: 'px-3 py-1.5 text-sm',
        md: 'px-3 py-2 text-sm',
        lg: 'px-4 py-2.5 text-base'
      }
      return sizes[this.size]
    }
  },
  methods: {
    handleInput(event) {
      let value = event.target.value

      if (this.type === 'number' && value !== '') {
        value = Number(value)
      }

      this.$emit('update:modelValue', value)
      this.$emit('input', value)
    },
    handleBlur(event) {
      this.$emit('blur', event)
    },
    handleFocus(event) {
      this.$emit('focus', event)
    },
    handleEnter(event) {
      this.$emit('enter', event)
    },
    handleClear() {
      this.$emit('update:modelValue', '')
      this.$emit('clear')
    }
  }
}
</script>
